import type { FeatureFlag } from '../feature-flag/models';

export const SUPPORTED_DOCUMENT_FILE_TYPES = [
    'apng', // Animated PNG image; opens in browsers and image viewers.
    'avif', // AV1 image; opens in modern browsers and image viewers.
    'bimx', // Graphisoft BIMx building model; opens in BIMx viewers.
    'bmp', // Bitmap image; opens in image viewers.
    'csv', // Comma-separated table; opens in Excel, Sheets, and text editors.
    'doc', // Legacy Word document; opens in Word and compatible editors.
    'docx', // Word document; opens in Word and compatible editors.
    'dwg', // AutoCAD drawing; opens in AutoCAD and DWG viewers.
    'gif', // GIF image or animation; opens in browsers and image viewers.
    'htm', // HTML web page; opens in browsers and text editors.
    'html', // HTML web page; opens in browsers and text editors.
    'ico', // Icon image; opens in browsers and icon/image editors.
    'jpeg', // JPEG image; opens in browsers and image viewers.
    'jpg', // JPEG image; opens in browsers and image viewers.
    'mpp', // Microsoft Project plan; opens in Project and compatible viewers.
    'pdf', // PDF document; opens in browsers and PDF readers.
    'png', // PNG image; opens in browsers and image viewers.
    'svg', // SVG vector image; opens in browsers and vector editors.
    'tif', // TIFF image; opens in image viewers and document scanners.
    'tiff', // TIFF image; opens in image viewers and document scanners.
    'vsdx', // Visio diagram; opens in Visio and compatible viewers.
    'xls', // Legacy Excel spreadsheet; opens in Excel and compatible editors.
    'xlsx', // Excel spreadsheet; opens in Excel and compatible editors.
] as const;

export type DocumentFileType = (typeof SUPPORTED_DOCUMENT_FILE_TYPES)[number];

export function isSupportedDocumentFileType(
    value: string
): value is DocumentFileType {
    return SUPPORTED_DOCUMENT_FILE_TYPES.includes(value as DocumentFileType);
}

export interface Scopes {
    quipexApi: string[];
}

export interface SessionConfig {
    inactiveTimeoutSeconds: number;
    idleTimeoutSeconds: number;
}

export interface SupportEmailConfig {
    recipient: string;
    subject: string;
}

export interface DatadogRumEnvironmentConfig {
    applicationId: string;
    clientToken: string;
    defaultPrivacyLevel?: string;
    sessionReplaySampleRate?: string;
    sessionSampleRate?: string;
}

export interface AuthorizedUsers {
    insights: string[];
}

export interface Environment {
    appVersion: string;
    env: 'development' | 'qa' | 'uat' | 'demo' | 'production';
    apiBase: string;
    apiFunctionBase: string;
    apiAIBase: string;
    scopes: Scopes;
    auth0ClientId: string;
    auth0Domain: string;
    auth0Audience: string;
    graphClientId: string;
    dropboxClientId: string;
    googleClientId: string;
    sessionConfig: SessionConfig;
    supportEmail: SupportEmailConfig;
    imagePreviewFileTypes: DocumentFileType[];
    documentUploadTypes: DocumentFileType[];
    maxFileSizeInMb: number;
    /** number of files that can be uploaded at one time */
    concurrentFilesUpload: number;
    instrumentationKey: string;
    datadogRum?: DatadogRumEnvironmentConfig;
    autoTagMaxSizeMB: number;
    featureFlags: Record<FeatureFlag, boolean>;
}

export function parseCommaSeparatedString(value: string): string[] {
    if (!value || typeof value !== 'string') {
        return [];
    }
    return value
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
}
